export const WARNING_SETTINGS_STORAGE_KEY = 'jtl-dashboard-warning-settings';
export const WARNING_SETTINGS_UPDATED_EVENT = 'jtl-dashboard-warning-settings-updated';

export const DEFAULT_WARNING_SETTINGS = {
    lowStockUnits: 5,
    topProductStockUnits: 5,
    returnRatePct: 8,
    channelMarginPct: 8,
    boardNotificationsEnabled: true,
    emailNotificationsEnabled: false,
    browserNotificationsEnabled: false,
    pushNotificationsEnabled: false,
    notificationEmail: '',
    recipientEmails: [],
    excludedMarketplaces: [],
    excludedProducts: [],
    escalationLevel: 'normal'
};

export const WARNING_ESCALATION_OPTIONS = [
    { value: 'normal', label: 'Normal', helper: 'Normale Warnung im Panel' },
    { value: 'important', label: 'Wichtig', helper: 'Stärker hervorgehoben' },
    { value: 'critical', label: 'Kritisch', helper: 'Als kritische Warnung markieren' }
];

const escalationValues = new Set(WARNING_ESCALATION_OPTIONS.map(option => option.value));

export const normalizeEscalationLevel = (value, fallback = DEFAULT_WARNING_SETTINGS.escalationLevel) => {
    const requested = String(value || '').trim();
    return escalationValues.has(requested) ? requested : fallback;
};

export const WARNING_THRESHOLD_FIELDS = [
    {
        key: 'lowStockUnits',
        label: 'Low Stock',
        helper: 'Artikelbestand unter',
        unit: 'Stk.',
        min: 1,
        max: 999,
        step: 1
    },
    {
        key: 'topProductStockUnits',
        label: 'Topseller Bestand',
        helper: 'Top-Produkte unter',
        unit: 'Stk.',
        min: 1,
        max: 999,
        step: 1
    },
    {
        key: 'returnRatePct',
        label: 'Retourenquote',
        helper: 'Warnung ab',
        unit: '%',
        min: 0,
        max: 100,
        step: 0.5
    },
    {
        key: 'channelMarginPct',
        label: 'Kanalmarge',
        helper: 'Warnung unter',
        unit: '%',
        min: -100,
        max: 100,
        step: 0.5
    }
];

export const WARNING_RULE_TYPE_OPTIONS = [
    {
        value: 'low_stock',
        label: 'Low Stock',
        settingKey: 'lowStockUnits',
        unit: 'Stk.',
        min: 1,
        max: 999,
        step: 1,
        supportedScopes: ['article_group']
    },
    {
        value: 'top_product_stock',
        label: 'Topseller Bestand',
        settingKey: 'topProductStockUnits',
        unit: 'Stk.',
        min: 1,
        max: 999,
        step: 1,
        supportedScopes: ['article_group', 'channel']
    },
    {
        value: 'return_rate',
        label: 'Retourenquote',
        settingKey: 'returnRatePct',
        unit: '%',
        min: 0,
        max: 100,
        step: 0.5,
        supportedScopes: ['channel', 'customer_group']
    },
    {
        value: 'channel_margin',
        label: 'Marge',
        settingKey: 'channelMarginPct',
        unit: '%',
        min: -100,
        max: 100,
        step: 0.5,
        supportedScopes: ['channel', 'customer_group']
    }
];

export const WARNING_SCOPE_OPTIONS = [
    { value: 'channel', label: 'Kanal', placeholder: 'Amazon, eBay, Shop' },
    { value: 'article_group', label: 'Artikelgruppe', placeholder: 'Gruppe oder Kategorie' },
    { value: 'customer_group', label: 'Kundengruppe', placeholder: 'Endkunden, Händler, DocMorris' }
];

export const WARNING_RULE_PRESETS = [
    {
        id: 'channel-margin',
        label: 'Kanalmarge',
        ruleType: 'channel_margin',
        scopeType: 'channel'
    },
    {
        id: 'channel-top-stock',
        label: 'Topseller Kanal',
        ruleType: 'top_product_stock',
        scopeType: 'channel'
    },
    {
        id: 'group-low-stock',
        label: 'Artikelgruppe Bestand',
        ruleType: 'low_stock',
        scopeType: 'article_group'
    },
    {
        id: 'customer-return-rate',
        label: 'Kundengruppe Retouren',
        ruleType: 'return_rate',
        scopeType: 'customer_group'
    }
];

const ruleTypeByValue = new Map(WARNING_RULE_TYPE_OPTIONS.map(option => [option.value, option]));

const normalizeStringList = (value) => {
    const raw = Array.isArray(value)
        ? value
        : String(value || '')
            .split(/[,\n;]/g);

    return Array.from(new Set(
        raw
            .map(item => String(item || '').trim())
            .filter(Boolean)
    ));
};

export const normalizeRecipientEmails = (settings = {}) => {
    const recipients = normalizeStringList(
        settings.recipientEmails
        ?? settings.recipient_emails
        ?? settings.recipients
        ?? settings.recipientList
        ?? []
    );
    if (recipients.length) return recipients;

    const fallbackEmail = String(settings.notificationEmail || settings.notification_email || '').trim();
    return fallbackEmail ? [fallbackEmail] : [];
};

export const normalizeExcludedMarketplaces = (settings = {}) => normalizeStringList(
    settings.excludedMarketplaces
    ?? settings.excluded_marketplaces
    ?? settings.marketplaceExclusions
    ?? []
);

export const normalizeExcludedProducts = (settings = {}) => normalizeStringList(
    settings.excludedProducts
    ?? settings.excluded_products
    ?? settings.productExclusions
    ?? []
);

const clampRuleThreshold = (ruleType, value, settings = {}) => {
    const definition = ruleTypeByValue.get(ruleType) || WARNING_RULE_TYPE_OPTIONS[0];
    const fallback = Number(settings[definition.settingKey] ?? DEFAULT_WARNING_SETTINGS[definition.settingKey]);
    const numeric = Number(value);
    const safeValue = Number.isFinite(numeric) ? numeric : fallback;
    return Math.min(definition.max, Math.max(definition.min, safeValue));
};

export const createWarningRule = (settings = {}, overrides = {}) => {
    const requestedRuleType = overrides.ruleType || overrides.rule_type || 'channel_margin';
    const definition = ruleTypeByValue.get(requestedRuleType) || WARNING_RULE_TYPE_OPTIONS[3];
    const scopeType = definition.supportedScopes.includes(overrides.scopeType || overrides.scope_type)
        ? overrides.scopeType || overrides.scope_type
        : definition.supportedScopes[0];

    return {
        ruleType: definition.value,
        scopeType,
        scopeValue: String(overrides.scopeValue ?? overrides.scope_value ?? '').trim(),
        thresholdValue: clampRuleThreshold(
            definition.value,
            overrides.thresholdValue ?? overrides.threshold_value ?? settings[definition.settingKey],
            settings
        ),
        isEnabled: overrides.isEnabled ?? overrides.is_enabled ?? true,
        boardEnabled: overrides.boardEnabled ?? overrides.board_enabled ?? settings.boardNotificationsEnabled ?? true,
        emailEnabled: overrides.emailEnabled ?? overrides.email_enabled ?? settings.emailNotificationsEnabled ?? false,
        browserEnabled: overrides.browserEnabled ?? overrides.browser_enabled ?? overrides.pushEnabled ?? overrides.push_enabled ?? settings.browserNotificationsEnabled ?? settings.pushNotificationsEnabled ?? false,
        pushEnabled: overrides.browserEnabled ?? overrides.browser_enabled ?? overrides.pushEnabled ?? overrides.push_enabled ?? settings.browserNotificationsEnabled ?? settings.pushNotificationsEnabled ?? false,
        escalationLevel: normalizeEscalationLevel(overrides.escalationLevel ?? overrides.escalation_level ?? settings.escalationLevel)
    };
};

export const createDefaultWarningRule = (settings = {}) => createWarningRule(settings);

export const normalizeWarningRule = (rule = {}, settings = {}) => {
    const requestedRuleType = String(rule.ruleType || rule.rule_type || '').trim();
    const definition = ruleTypeByValue.get(requestedRuleType) || WARNING_RULE_TYPE_OPTIONS[0];
    const requestedScope = String(rule.scopeType || rule.scope_type || '').trim();
    const scopeType = definition.supportedScopes.includes(requestedScope)
        ? requestedScope
        : definition.supportedScopes[0];

    return {
        id: rule.id || null,
        ruleType: definition.value,
        scopeType,
        scopeValue: String(rule.scopeValue ?? rule.scope_value ?? '').trim().slice(0, 160),
        thresholdValue: clampRuleThreshold(
            definition.value,
            rule.thresholdValue ?? rule.threshold_value,
            settings
        ),
        isEnabled: rule.isEnabled ?? rule.is_enabled ?? true,
        boardEnabled: rule.boardEnabled ?? rule.board_enabled ?? rule.metadata?.board_enabled ?? settings.boardNotificationsEnabled ?? true,
        emailEnabled: rule.emailEnabled ?? rule.email_enabled ?? false,
        browserEnabled: rule.browserEnabled ?? rule.browser_enabled ?? rule.pushEnabled ?? rule.push_enabled ?? false,
        pushEnabled: rule.browserEnabled ?? rule.browser_enabled ?? rule.pushEnabled ?? rule.push_enabled ?? false,
        escalationLevel: normalizeEscalationLevel(rule.escalationLevel ?? rule.escalation_level ?? settings.escalationLevel),
        metadata: rule.metadata && typeof rule.metadata === 'object' ? rule.metadata : {}
    };
};

export const normalizeWarningRules = (rules = [], settings = {}) => (
    (Array.isArray(rules) ? rules : [])
        .map(rule => normalizeWarningRule(rule, settings))
        .filter(rule => rule.scopeValue)
);

export const clampWarningSettings = (settings = {}) => {
    const next = { ...DEFAULT_WARNING_SETTINGS };

    WARNING_THRESHOLD_FIELDS.forEach((field) => {
        const value = Number(settings[field.key]);
        const fallback = DEFAULT_WARNING_SETTINGS[field.key];
        const safeValue = Number.isFinite(value) ? value : fallback;
        next[field.key] = Math.min(field.max, Math.max(field.min, safeValue));
    });

    next.boardNotificationsEnabled = settings.boardNotificationsEnabled ?? settings.board_enabled ?? true;
    next.boardNotificationsEnabled = next.boardNotificationsEnabled !== false;
    next.emailNotificationsEnabled = Boolean(settings.emailNotificationsEnabled ?? settings.email_enabled);
    next.browserNotificationsEnabled = Boolean(settings.browserNotificationsEnabled ?? settings.browser_enabled ?? settings.pushNotificationsEnabled ?? settings.push_enabled);
    next.pushNotificationsEnabled = next.browserNotificationsEnabled;
    next.recipientEmails = normalizeRecipientEmails(settings);
    next.notificationEmail = next.recipientEmails[0] || String(settings.notificationEmail || '').trim();
    next.excludedMarketplaces = normalizeExcludedMarketplaces(settings);
    next.excludedProducts = normalizeExcludedProducts(settings);
    next.escalationLevel = normalizeEscalationLevel(settings.escalationLevel ?? settings.escalation_level);

    return next;
};

export const readWarningSettings = () => {
    if (typeof window === 'undefined') return DEFAULT_WARNING_SETTINGS;

    try {
        const current = window.localStorage.getItem(WARNING_SETTINGS_STORAGE_KEY);
        const legacy = window.localStorage.getItem('jtl-dashboard-warning-thresholds');
        const stored = current || legacy;
        return clampWarningSettings(stored ? JSON.parse(stored) : DEFAULT_WARNING_SETTINGS);
    } catch {
        return DEFAULT_WARNING_SETTINGS;
    }
};

export const writeWarningSettings = (settings) => {
    const next = clampWarningSettings(settings);
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(WARNING_SETTINGS_STORAGE_KEY, JSON.stringify(next));
        window.dispatchEvent(new CustomEvent(WARNING_SETTINGS_UPDATED_EVENT, { detail: next }));
    }
    return next;
};
